import { Component, OnDestroy, OnInit } from '@angular/core';
import { Branch } from '../branch';
import { BranchChart } from '../branch-chart';
import { ActivatedRoute, Router } from '@angular/router';
import { BranchesService } from '../branches.service';
import { Location } from '@angular/common';
import { ShiftHolderChart } from '../shift-holder-chart';
import { SiteCountry, SiteTotalService } from '../site-total.service';
import { forkJoin } from 'rxjs';
import { ThemeService } from '../theme.service';

@Component({
  selector: 'app-branches',
  templateUrl: './branches.component.html',
  styleUrls: ['./branches.component.scss']
})
export class BranchesComponent implements OnInit, OnDestroy {

  branchItems: Branch[] = [];
  branchChart: BranchChart[] = [];
  country = '';
  currDate: Date = new Date();
  refreshTimer: any;

  // key: site ID, value: array of SiteCountry type
  sitesBranches: Record<string, SiteCountry[]> = {};
  // key: site ID, value: inspector IDs
  futureSiteInspectors: Record<number, number[]> = {};
  // key: site ID, value: positions
  positions: Record<number, any[]> = {};

  constructor(
    private route: ActivatedRoute,
    private branchesService: BranchesService,
    private sitesTotalService: SiteTotalService,
    public themeService: ThemeService,
    private location: Location,
    private router: Router
  ) {
    // this.route.params.subscribe(event => {
    //   this.ngOnInit();
    // });
  }

  ngOnInit() {
    if (localStorage.getItem('token') === null) {
      this.router.navigate(['/login']);
    }
    if (localStorage.getItem('selectedDate') !== null) {
      this.currDate = new Date(localStorage.getItem('selectedDate'));
    } else {
      localStorage.setItem('selectedDate', this.getFormattedDate(this.currDate));
    }
    localStorage.removeItem('branch');
    localStorage.removeItem('siteId');

    this.country = this.route.snapshot.paramMap.get('country');
    if (this.country) {
      this.country = this.country;
      localStorage.setItem('country', this.country);
    } else {
      this.country = 'ALL';
    }

    this.getBranchData(this.country);
    this.getCountriesSites();
    this.refreshTimer = setInterval(() => {
      this.getBranchData(this.country);
      this.getCountriesSites();
    }, 1000 * 60 * 10);
  }

  ngOnDestroy() {
    clearInterval(this.refreshTimer);
  }

  getCountriesSites(): void {
    // Clear data
    this.sitesBranches = {};
    this.futureSiteInspectors = {};
    this.positions = {};

    const selectedDate = new Date(this.currDate);
    selectedDate.setDate(selectedDate.getDate() + 1);

    const currentDate = this.getFormattedDate(this.currDate);
    const nextDayDate = this.getFormattedDate(selectedDate);

    // forkJoin is like Promise.all, but it's used in Angular instead
    forkJoin([
      this.sitesTotalService.getSitesAssignments(currentDate),
      this.sitesTotalService.getSitesPositions(currentDate),
      this.sitesTotalService.getFutureInspectorIds(nextDayDate),
      this.sitesTotalService.getSitesCountries(currentDate)
    ]).subscribe(([currentSites, currentPositions, futureInspectors, countriesSites]) => {

      const localFutureInspectors: Record<number, number[]> = {};

      futureInspectors.forEach(assignment => {
        // Fill futureSiteInspectors key with the assignment site ID and fill the value with its inspector IDs
        localFutureInspectors[assignment.site] = assignment.inspectorIds.split(',').map(inspectorId => Number(inspectorId));
      });

      this.futureSiteInspectors = localFutureInspectors;

      // sites will hold records of site IDs as a key and site assignments as a value
      const sites: Record<number, any[]> = {};

      currentSites.forEach(assignment => {
        // If key exists then push the data
        if (sites[assignment.site]) {
          sites[assignment.site].push(assignment);
        } else {
          // If it doesn't exist, then repeat the same process above but instead initialize the array
          sites[assignment.site] = [assignment];
        }
      });

      const localPositions: Record<number, any[]> = {};

      // Same process as the top currentSites
      currentPositions.forEach(position => {
        // If key exists then push the data
        if (localPositions[position.site]) {
          localPositions[position.site].push(position);
        } else {
          // If it doesn't exist, then repeat the same process above but instead initialize the array
          localPositions[position.site] = [position];
        }
      });

      this.positions = localPositions;

      const localSitesBranches: Record<string, SiteCountry[]> = {};

      countriesSites.forEach(countryAssignment => {
        const branchSites = {
          ...countryAssignment,
          sites: sites[countryAssignment.siteId]
        };

        // If key exists then push the data
        if (localSitesBranches[countryAssignment.branchName]) {
          // Sometimes branches have the same siteId so we gotta make sure we don't push those records if they already exist
          if (!localSitesBranches[countryAssignment.branchName].some(({ siteId }) => siteId === countryAssignment.siteId)) {
            localSitesBranches[countryAssignment.branchName].push(branchSites);
          }
        } else {
          // If it doesn't exist, then repeat the same process above but instead initialize the array
          localSitesBranches[countryAssignment.branchName] = [branchSites];
        }

        // Below if condition will only allow records that match the selected country in the URL
        // In case none (case "ALL") is selected, then allow everything
        if (countryAssignment.country === this.country || this.country === 'ALL') {
          // Add "ALL" option to sitesCountries that will include everything
          if (localSitesBranches['ALL']) {
            // Same condition up there that checks for duplicated site IDs
            if (!localSitesBranches['ALL'].some(({ siteId }) => siteId === countryAssignment.siteId)) {
              localSitesBranches['ALL'].push(branchSites);
            }
          } else {
            localSitesBranches['ALL'] = [branchSites];
          }
        }
      });

      this.sitesBranches = localSitesBranches;
    });
  }

  getBranchData( country: string ): void {
    this.branchesService.getBranchesData(this.country, this.getFormattedDate(this.currDate))
      .subscribe((data) => {
        this.compileBranchesData(data);
      });
  }

  changeDate( t: any ): void {
    this.currDate = t.value;
    localStorage.setItem('selectedDate', t.value);
    this.getBranchData(this.country);
    this.getCountriesSites();
  }

  getFormattedDate( dt: Date ): string {
    let m: any = dt.getMonth() + 1;
    m = (m < 10) ? '0' + m.toString() : m.toString();
    let d: any = dt.getDate();
    d = (d < 10) ? '0' + d.toString() : d.toString();

    return dt.getFullYear().toString() + '-' + m.toString() + '-' + d.toString();
  }

  compileBranchesData( data: any ) {
    this.branchChart = [];

    if (data.length === 0) {
      return;
    }

    let shiftItem: ShiftHolderChart = new ShiftHolderChart();
    let tempHit = 0;
    let tempTotal = 0;
    let tempFilled = 0;
    const tempData = JSON.parse(JSON.stringify(data));
    let tempBranchChart: BranchChart = new BranchChart();
    let tempName = 'ALL';
    tempBranchChart.branchName = 'ALL';

    // Sort by shift, then filled
    tempData.sort(function (a, b) {
      const aConcat = a.shift + a.isFilled;
      const bConcat = b.shift + b.isFilled;

      if (aConcat > bConcat) {
        return 1;
      } else if (aConcat < bConcat) {
        return -1;
      } else {
        return 0;
      }
    });

    // Populate values for ALL item
    for (const item of tempData) {
      if (tempName === '') {
        tempName = 'ALL';
        tempBranchChart.branchName = 'ALL';
        tempBranchChart.shifts.push(item);
        tempTotal += parseFloat(item.qty);
        if (item.isFilled === 1) {
          tempFilled += parseFloat(item.qty);
        }
      } else {
        for (const shift of tempBranchChart.shifts) {
          if (shift.shift === item.shift && shift.isFilled === item.isFilled) {
            tempHit = 1;
            shift.qty += item.qty;
          }
        }
        if (tempHit === 1) {
          tempHit = 0;
        } else {
          tempBranchChart.shifts.push(item);
        }
        tempTotal += parseFloat(item.qty);
        if (item.isFilled === 1) {
          tempFilled += parseFloat(item.qty);
        }
      }
    }
    this.branchItems = data;
    tempBranchChart.total = tempTotal;
    tempBranchChart.filled = tempFilled;
    this.branchChart.push(tempBranchChart);

    // Populate values for Branch items
    tempName = '';
    tempBranchChart = new BranchChart();
    shiftItem = new ShiftHolderChart();
    tempHit = 0;
    tempTotal = 0;
    tempFilled = 0;

    for (const item of data) {
      if (tempName === '') {
        tempName = item.branch;
        tempBranchChart.branchName = item.branch;
        tempBranchChart.shifts.push(item);
        tempTotal += parseFloat(item.qty);
        if (item.isFilled === 1) {
          tempFilled += parseFloat(item.qty);
        }
      } else {
        if (tempName === item.branch) {
          tempBranchChart.shifts.push(item);
          tempTotal += parseFloat(item.qty);
          if (item.isFilled === 1) {
            tempFilled += parseFloat(item.qty);
          }
        } else {
          tempBranchChart.total = tempTotal;
          tempBranchChart.filled = tempFilled;
          this.branchChart.push(tempBranchChart);
          tempBranchChart = new BranchChart();
          tempTotal = 0;
          tempFilled = 0;
          tempName = item.branch;
          tempBranchChart.branchName = item.branch;
          tempBranchChart.shifts.push(item);
          tempTotal += parseFloat(item.qty);
          if (item.isFilled === 1) {
            tempFilled += parseFloat(item.qty);
          }
        }
      }
    }
    this.branchItems = data;
    tempBranchChart.total = tempTotal;
    tempBranchChart.filled = tempFilled;
    this.branchChart.push(tempBranchChart);

    tempName = '';
    tempTotal = 0;
    let shiftTotals = [];
    for (const branch of this.branchChart) {
      shiftTotals = [];
      let tempOb = [];
      for (const item of branch.shifts) {
        if (item.shift === tempName) {
          tempTotal += item.qty;
        } else {
          tempOb = [tempName, tempTotal];
          shiftTotals.push(tempOb);
          tempName = item.shift;
          tempTotal = item.qty;
        }
      }
      tempOb = [tempName, tempTotal];
      shiftTotals.push(tempOb);
      for (const item of branch.shifts) {
        for (const shiftTotal of shiftTotals) {
          if (item.shift === shiftTotal[0]) {
            item.shiftTotal = shiftTotal[1];
          }
        }
      }
    }
    this.sortBranches();
  }

  goBack(): void {
    this.location.back();
  }

  goSites(item: any) {
    this.router.navigate(['sites/' + item]);
  }

  filter( data: any[] ): any[] {
    const sc: BranchChart[] = [];
    data.forEach(function(d) {
      if (d.branchName.toUpperCase().indexOf((<HTMLInputElement>document.getElementById('search')).value.toUpperCase()) !== -1) {
        sc.push(d);
      }
    });
    return sc;
  }

  sortBranches() {
    this.branchChart.sort((a, b) => {
      // ensure that ALL is returned at first
      if (a.branchName === 'ALL') {
        return -1;
      } else if (b.branchName === 'ALL') {
        return 1;
      }
      if (a.total === 0 && b.total === 0) { // Both totals are 0, so seperate sort for alpha
        if (a.branchName < b.branchName) {
          return -1;
        } else {
          return 1;
        }
      } else if (a.total === 0) { // Only a is 0, send to end
        return 1;
      } else if (b.total === 0) { // Only b is 0, send to end
        return -1;
      } else { // Alpha sort, both items have values
        if (a.branchName < b.branchName) {
          return -1;
        } else {
          return 1;
        }
      }
    });
  }

}
